import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { QuoteSelector } from './quote-selector';
import { ModelSelector } from './model-selector';
import { RiskGauge, RiskBar } from './risk-gauge';
import { useAiIntelligenceStore } from '../store/ai-intelligence-store';
import { Shield, BarChart3, AlertTriangle } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const PIE_COLORS = ['#ef4444', '#f59e0b', '#10b981'];

export function PredictionsTab() {
  const [quoteId, setQuoteId] = useState('');
  const [modelId, setModelId] = useState('');

  const {
    approvalScore,
    isLoadingScore,
    scoreError,
    fetchApprovalScore,
    modelAnalysis,
    isLoadingModelAnalysis,
    modelAnalysisError,
    fetchModelAnalysis,
  } = useAiIntelligenceStore();

  const handleScoreCalc = () => {
    if (!quoteId) return;
    fetchApprovalScore(quoteId);
  };

  const handleModelAnalysis = () => {
    if (!modelId) return;
    fetchModelAnalysis(modelId);
  };

  // Parse distribution for pie chart
  const distData = modelAnalysis?.distribucionMargenes
    ? [
        { name: 'Bajo Piso', value: parseInt(modelAnalysis.distribucionMargenes.bajoPiso) || 0 },
        {
          name: 'Entre Piso y Objetivo',
          value: parseInt(modelAnalysis.distribucionMargenes.entreTargetYPiso) || 0,
        },
        {
          name: 'Sobre Objetivo',
          value: parseInt(modelAnalysis.distribucionMargenes.sobreTarget) || 0,
        },
      ].filter((d) => d.value > 0)
    : [];

  return (
    <div className="space-y-8">
      {/* ── Sección 1: Score de Aprobación ── */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Score de Riesgo de Aprobación
          </h3>
          <p className="text-sm text-muted-foreground mt-1 ml-7">
            Calcula un puntaje de 0 a 100 que indica que tan riesgosa es una cotización para el
            aprobador. Un score <strong className="text-foreground">bajo (0-30)</strong> es de bajo
            riesgo y podria aprobarse automaticamente. Un score{' '}
            <strong className="text-foreground">alto (70-100)</strong> requiere revision detallada.
            Se evaluan 4 factores: margen vs objetivo, descuento otorgado, historial del vendedor y
            monto total.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 items-end">
              <QuoteSelector value={quoteId} onChange={setQuoteId} />
              <Button onClick={handleScoreCalc} disabled={!quoteId || isLoadingScore}>
                {isLoadingScore ? 'Calculando...' : 'Calcular Score'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {isLoadingScore && <Skeleton className="h-64" />}

        {scoreError && (
          <Card className="border-destructive/50">
            <CardContent className="pt-6 flex items-center gap-3 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              <p>{scoreError}</p>
            </CardContent>
          </Card>
        )}

        {approvalScore && !isLoadingScore && (
          <div className="grid grid-cols-1 lg:grid-cols-[auto,1fr] gap-6">
            {/* Gauge */}
            <Card>
              <CardContent className="pt-6 flex flex-col items-center justify-center min-w-[200px]">
                <RiskGauge score={approvalScore.scoreTotal} nivel={approvalScore.nivel} size="lg" />
                <p className="text-sm text-muted-foreground mt-3">
                  {approvalScore.cotizacion} — {approvalScore.cliente}
                </p>
              </CardContent>
            </Card>

            {/* Desglose */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Desglose de Riesgo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RiskBar
                  label="Riesgo de Margen"
                  puntos={approvalScore.desglose.riesgoMargen.puntos}
                  max={approvalScore.desglose.riesgoMargen.max}
                  detalle={approvalScore.desglose.riesgoMargen.detalle}
                />
                <RiskBar
                  label="Riesgo de Descuento"
                  puntos={approvalScore.desglose.riesgoDescuento.puntos}
                  max={approvalScore.desglose.riesgoDescuento.max}
                  detalle={approvalScore.desglose.riesgoDescuento.detalle}
                />
                <RiskBar
                  label="Historial Vendedor"
                  puntos={approvalScore.desglose.historialVendedor.puntos}
                  max={approvalScore.desglose.historialVendedor.max}
                  detalle={approvalScore.desglose.historialVendedor.detalle}
                />
                <RiskBar
                  label="Riesgo de Monto"
                  puntos={approvalScore.desglose.riesgoMonto.puntos}
                  max={approvalScore.desglose.riesgoMonto.max}
                  detalle={approvalScore.desglose.riesgoMonto.detalle}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {!approvalScore && !isLoadingScore && !scoreError && (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground py-8">
              <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm max-w-md mx-auto">
                Selecciona una cotizacion arriba y presiona &quot;Calcular Score&quot;. Veras un
                medidor de riesgo con el desglose de cada factor que contribuye al puntaje.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Sección 2: Análisis de Modelo ── */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Análisis de Modelo de Pricing
          </h3>
          <p className="text-sm text-muted-foreground mt-1 ml-7">
            Evalua que tan bien esta funcionando un modelo de pricing basandote en las cotizaciones
            reales que lo usaron. Muestra: como se distribuyen los margenes (bajo piso, entre piso y
            objetivo, sobre objetivo), tasas de aprobacion/rechazo, variables que nunca se modifican
            (posibles candidatas a eliminar), y recomendaciones de optimizacion.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 items-end">
              <ModelSelector value={modelId} onChange={setModelId} />
              <Button onClick={handleModelAnalysis} disabled={!modelId || isLoadingModelAnalysis}>
                {isLoadingModelAnalysis ? 'Analizando...' : 'Analizar Modelo'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {isLoadingModelAnalysis && <Skeleton className="h-64" />}

        {modelAnalysisError && (
          <Card className="border-destructive/50">
            <CardContent className="pt-6 flex items-center gap-3 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              <p>{modelAnalysisError}</p>
            </CardContent>
          </Card>
        )}

        {modelAnalysis && !isLoadingModelAnalysis && (
          <div className="space-y-4">
            {/* KPIs del modelo */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MetricCard label="Cotizaciones" value={String(modelAnalysis.totalCotizaciones)} />
              <MetricCard
                label="Margen Promedio"
                value={modelAnalysis.distribucionMargenes?.margenPromedio || 'N/A'}
              />
              <MetricCard
                label="Margen Mediano"
                value={modelAnalysis.distribucionMargenes?.margenMediano || 'N/A'}
              />
              <MetricCard
                label="Tasa Aprobación"
                value={modelAnalysis.tasas?.aprobacion || 'N/A'}
              />
              <MetricCard label="Tasa Rechazo" value={modelAnalysis.tasas?.rechazo || 'N/A'} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart de distribución */}
              {distData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Distribución de Márgenes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={distData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {distData.map((_entry, idx) => (
                            <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Recomendaciones */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recomendaciones</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {modelAnalysis.recomendaciones.map((rec, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <span className="text-primary mt-0.5">•</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Variables que nunca cambian */}
            {modelAnalysis.variables?.nuncaCambian && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground mb-2">
                    Variables que nunca se modifican del valor por defecto:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {modelAnalysis.variables.nuncaCambian.map((v) => (
                      <span key={v} className="px-2 py-1 bg-muted rounded text-xs font-mono">
                        {v}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {!modelAnalysis && !isLoadingModelAnalysis && !modelAnalysisError && (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground py-8">
              <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm max-w-md mx-auto">
                Selecciona un modelo de pricing arriba para ver su análisis completo. Los modelos
                son las plantillas de calculo que usas al crear cotizaciones (ej: &quot;Servicios
                Profesionales&quot;, &quot;Infraestructura TI&quot;).
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-lg border bg-card text-center">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
